import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Common";
import { Toolbar, TableShell, FormModal, TextField, TextArea, SelectField } from "@/components/FormKit";
import { useAuth } from "@/context/AuthContext";
import { Pencil, Loader2, CornerDownRight } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", type: "despesa", parent_id: "none", description: "", status: "ativo" };

export default function CategoriesPage() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => { setLoading(true); api.get("/categories").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    const payload = { ...form, parent_id: form.parent_id && form.parent_id !== "none" ? form.parent_id : null };
    try {
      if (editing) await api.put(`/categories/${editing.id}`, payload);
      else await api.post("/categories", payload);
      toast.success("Categoria salva."); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSubmitting(false); }
  };

  const nameById = useMemo(() => Object.fromEntries(rows.map((r) => [r.id, r.name])), [rows]);
  const parentOptions = useMemo(
    () => [{ value: "none", label: "Nenhuma (categoria principal)" }].concat(
      rows.filter((r) => r.type === form.type && r.id !== editing?.id).map((r) => ({ value: r.id, label: r.name }))
    ), [rows, form.type, editing]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) => (r.name || "").toLowerCase().includes(q));
  }, [rows, search]);
  const canEdit = hasPermission("categories.update");

  return (
    <div className="space-y-6">
      <PageHeader title="Categorias Financeiras" subtitle="Classificação hierárquica de receitas e despesas" />
      <Toolbar search={search} setSearch={setSearch} searchTestId="cat-search"
        onNew={hasPermission("categories.create") ? () => { setEditing(null); setForm(EMPTY); setOpen(true); } : null}
        newLabel="Nova Categoria" newTestId="add-category-button" />

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
        : filtered.length === 0 ? <EmptyState text="Nenhuma categoria cadastrada." />
        : (
        <TableShell testId="categories-table" headers={[{ label: "Nome" }, { label: "Tipo" }, { label: "Categoria Superior" }, { label: "Status" }, { label: "Ações", align: "right" }]}>
          {filtered.map((r) => (
            <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`cat-row-${r.id}`}>
              <td className="px-4 py-3.5 font-medium text-slate-800">
                <span className="flex items-center gap-1.5">{r.parent_id && <CornerDownRight className="h-3.5 w-3.5 text-slate-300" />}{r.name}</span>
              </td>
              <td className="px-4 py-3.5">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${r.type === "receita" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gold-light text-gold border-gold-border"}`}>
                  {r.type === "receita" ? "Receita" : "Despesa"}
                </span>
              </td>
              <td className="px-4 py-3.5 text-slate-500">{r.parent_id ? nameById[r.parent_id] || "—" : "—"}</td>
              <td className="px-4 py-3.5"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-3.5 text-right">
                {canEdit && <button onClick={() => { setEditing(r); setForm({ ...EMPTY, ...r, parent_id: r.parent_id || "none" }); setOpen(true); }} data-testid={`edit-cat-${r.id}`} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"><Pencil className="h-4 w-4" /></button>}
              </td>
            </tr>
          ))}
        </TableShell>
      )}

      <FormModal open={open} onOpenChange={setOpen} title={editing ? "Editar Categoria" : "Nova Categoria"} onSubmit={submit} submitting={submitting} testId="cat-form" width="max-w-lg">
        <SelectField label="Tipo" value={form.type} onChange={(v) => setForm((f) => ({ ...f, type: v, parent_id: "none" }))} testId="cat-type" options={[{ value: "receita", label: "Receita" }, { value: "despesa", label: "Despesa" }]} />
        <TextField label="Nome" value={form.name} onChange={(v) => set("name", v)} testId="cat-name" required />
        <SelectField label="Categoria Superior (opcional)" value={form.parent_id} onChange={(v) => set("parent_id", v)} testId="cat-parent" options={parentOptions} />
        <TextArea label="Descrição" value={form.description} onChange={(v) => set("description", v)} testId="cat-description" />
        <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} testId="cat-status" options={[{ value: "ativo", label: "Ativo" }, { value: "inativo", label: "Inativo" }]} />
      </FormModal>
    </div>
  );
}
